import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLionAutoStatus, useUpdateLionAutoConfig } from '../../hooks/useLionAuto';
import { detailLines } from './LaMatchResults';
import LaDiscordCard from './LaDiscordCard';
import {
  runnerStatusInfo, isDryRun, isEnabled, modeBanner, sessionText, configChangeConfirm, cadenceRows,
  dueText, agoText, easternText, soonestDue, lastStatusInfo, describeConfigError, errorText,
  autoLoginHint, ADMIN_DATA_PATH, maxChangeText, maxWritesText, maxChangeTitle, maxWritesTitle,
  PRESALE_RULE_TEXT, presaleChip, presaleSyncText,
} from '../../utils/lionAutoRunner';

// The Lion Auto runner, for admin-like users only (the page mounts it for
// them; a 403 hides it): one line always on screen (what the runner is doing,
// DRY RUN vs LIVE, this event's groups on Auto and the next one due) that
// opens onto the master switch, the dry-run switch, the "Reprice after a
// sale" default (the fast refill after a sale, for every show and artist
// without its own setting in its show's Reprice after a sale box; off by
// default, no confirm: it changes when a group runs, not whether it
// writes), the Auto login(s), the last
// run, the optional per-run limits (largest move, writes: "no limit" unless an
// admin set one through the API), the counters since the server started, each of
// this event's groups on Auto with its next run (a "presale until Oct 24" chip
// while it is on the presale schedule), the cadence and the presale rule under it
// (read-only: set on the server), and when Lion Checker's presale dates were
// last read (older servers send none: no line). Turning dry run off, or the
// master switch on while dry run is off, asks first: from then on prices go to Skybox with no
// one watching. The status re-reads every 30 s only while the panel is open
// (and the tab visible); folded, it reads once and on window focus.
// While the runner has no Auto login (no_session) or Skybox refused every one
// (paused_auth), a line under the head points to Admin → Data, where a super
// admin assigns or renews them (a link for super admins: canAssignLogin).

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

function LaAutoPanel({ eventId, canAssignLogin = false }) {
  const [open, setOpen] = useState(false);
  const statusQ = useLionAutoStatus({ poll: open });
  const configM = useUpdateLionAutoConfig();
  const [configError, setConfigError] = useState(null); // string[] | null

  const httpStatus = statusQ.error?.response?.status;
  // Not an admin after all (role changed since login): nothing to show
  if (httpStatus === 403) return null;

  const s = statusQ.data || null;
  const info = s ? runnerStatusInfo(s.status) : null;
  const dryRun = s ? isDryRun(s) : true;
  const enabled = s ? isEnabled(s) : false;
  const banner = modeBanner(dryRun);
  const session = sessionText(s?.session, s?.sessions);
  const allGroups = Array.isArray(s?.groups) ? s.groups : [];
  const here = allGroups.filter(g => String(g.eventId) === String(eventId));
  const others = allGroups.filter(g => String(g.eventId) !== String(eventId));
  const config = s?.config || {};
  const since = s?.sinceBoot || {};
  const loginHint = s ? autoLoginHint(s.status) : null;
  const presaleSync = presaleSyncText(s?.presaleSync);

  const toggleOpen = () => {
    setOpen(o => !o);
    if (!open) statusQ.refetch();
  };

  const change = (body) => {
    const ask = configChangeConfirm(body, { enabled, dryRun });
    if (ask && !window.confirm(ask)) return;
    setConfigError(null);
    configM.mutate(body, {
      onError: (err) => setConfigError([describeConfigError(err), ...detailLines(err?.response?.data?.details)]),
    });
  };

  let headline;
  if (statusQ.isLoading) headline = 'Reading the runner…';
  else if (httpStatus === 404) headline = 'Not available on this server yet';
  else if (statusQ.error && !s) headline = "Could not read the runner's status";
  else if (here.length) {
    const next = soonestDue(here);
    headline = `${plural(here.length, 'group')} on Auto here · next run ${next ? dueText(next) : 'not scheduled'}`;
  } else headline = 'No group on Auto for this event';

  return (
    <div className="la-panel la-auto-panel">
      <div className="la-panel-head la-auto-head">
        <button type="button" className="la-auto-toggle" onClick={toggleOpen} aria-expanded={open} title={open ? 'Fold the Auto runner' : 'Show the Auto runner'}>
          <span className="la-advanced-caret">{open ? '▾' : '▸'}</span>
          <h3>Auto runner</h3>
        </button>
        {info && <span className={`la-run-chip ${info.kind}`} title={info.text}>{info.label}</span>}
        {s && <span className={`la-mode-banner ${banner.live ? 'live' : 'dry'}`} title={banner.title}>{banner.text}</span>}
        <span className="la-muted">{headline}</span>
        {statusQ.error && !s && httpStatus !== 404 && (
          <button type="button" className="btn btn-secondary btn-sm la-push-right" onClick={() => statusQ.refetch()}>Retry</button>
        )}
      </div>
      {loginHint && (
        <div className="la-hint warn la-auto-login-hint">
          {loginHint}{' '}
          {canAssignLogin ? (
            <Link to={ADMIN_DATA_PATH} target="_blank" rel="noopener noreferrer">Admin → Data</Link>
          ) : (
            <strong>Admin → Data</strong>
          )}
          .
        </div>
      )}

      {open && s && (
        <div className="la-auto-body">
          <div className={`la-status ${info.kind === 'bad' ? 'error' : info.kind === 'warn' ? 'warn' : ''}`}>
            {info.text}
            {s.pausedSince && <span> Since {agoText(s.pausedSince)}.</span>}
            {errorText(s.lastError) && <span className="la-auto-error"> Last error: {errorText(s.lastError)}</span>}
          </div>

          <div className="la-inline">
            <label
              className="la-check"
              title={enabled ? 'Stop every Auto run on the server' : 'Let the server run the groups on Auto on their schedule'}
            >
              <input
                type="checkbox"
                checked={enabled}
                disabled={configM.isPending}
                onChange={(e) => change({ enabled: e.target.checked })}
              />
              <strong>Enabled</strong>
              <span className="la-hint">master switch</span>
            </label>
            <label
              className="la-check"
              title={dryRun ? 'Turning this off makes Auto write prices to Skybox' : 'Runs record what they would write and write nothing'}
            >
              <input
                type="checkbox"
                checked={dryRun}
                disabled={configM.isPending}
                onChange={(e) => change({ dryRun: e.target.checked })}
              />
              <strong>Dry run</strong>
            </label>
            <label
              className="la-check"
              title={'The default for every show and artist without its own setting (each show\'s "Reprice after a sale" box). '
                + 'On: a group on Auto re-ladders within a few minutes after one of its listings sells. Off: it waits for its next scheduled run.'}
            >
              <input
                type="checkbox"
                checked={config.saleRefill === true}
                disabled={configM.isPending}
                onChange={(e) => change({ saleRefill: e.target.checked })}
              />
              <strong>Reprice after a sale</strong>
              <span className="la-hint">default</span>
            </label>
            <span className={`la-hint${session.kind === 'bad' ? ' warn' : ''}`}>{session.text}</span>
            {configM.isPending && <span className="la-hint">Saving…</span>}
          </div>
          {configError && (
            <div className="la-status error">
              {configError[0]}
              {configError.length > 1 && (
                <ul className="la-detail-list">{configError.slice(1).map((d, i) => <li key={i}>{d}</li>)}</ul>
              )}
            </div>
          )}

          <div className="la-auto-cols">
            <dl className="la-summary">
              <dt>Last run</dt>
              <dd title={easternText(s.lastRunAt) || undefined}>{agoText(s.lastRunAt)}</dd>
              <dt>Last check</dt>
              <dd title={easternText(s.lastTickAt) || undefined}>{agoText(s.lastTickAt)}</dd>
              <dt>Largest move per run</dt>
              <dd title={maxChangeTitle(config.maxChangePct)}>{maxChangeText(config.maxChangePct)}</dd>
              <dt>Writes per run</dt>
              <dd title={maxWritesTitle(config.maxWrites)}>{maxWritesText(config.maxWrites)}</dd>
              <dt>Since restart</dt>
              <dd>
                {plural(num(since.runs), 'run')} · {num(since.repriced)} repriced · {num(since.failed)} failed ·{' '}
                {plural(num(since.dryRuns), 'dry run')}
              </dd>
              {presaleSync && (
                <>
                  <dt>Presale dates</dt>
                  <dd
                    title={"Lion Checker's Ticketmaster presale and public onsale dates for the events on Auto, read every hour. "
                      + 'Unresolved: Auto events whose Vivid link could not be read from Skybox this hour; their stored dates keep applying.'}
                  >
                    {presaleSync}
                  </dd>
                </>
              )}
            </dl>

            <div>
              <table className="la-auto-table">
                <thead>
                  <tr><th>Time to the show's start</th><th>Re-ladder every</th></tr>
                </thead>
                <tbody>
                  {cadenceRows(config.cadence).map(r => (
                    <tr key={r.key}><td>{r.label}</td><td>{r.every}</td></tr>
                  ))}
                  <tr><td>More than 2 h after the start</td><td>never</td></tr>
                </tbody>
              </table>
              <div className="la-hint">Times are to the show's start in the venue's time zone.</div>
              <div
                className="la-hint"
                title="On a show Ticketmaster lists with a presale, this replaces the table for that window; after it, the table applies."
              >
                {PRESALE_RULE_TEXT}
              </div>
            </div>
          </div>

          {here.length > 0 ? (
            <table className="la-auto-table">
              <thead>
                <tr><th>On Auto here</th><th>Last run</th><th>Next run</th></tr>
              </thead>
              <tbody>
                {here.map(g => {
                  const last = lastStatusInfo(g.autoLastStatus);
                  const presale = presaleChip(g.presale);
                  return (
                    <tr key={g.id}>
                      <td>{g.name}</td>
                      <td title={easternText(g.autoLastRunAt) || undefined}>
                        {agoText(g.autoLastRunAt)}
                        {g.autoLastRunAt && last.word && <span className={`la-auto-chip ${last.kind}`}>{last.word}</span>}
                      </td>
                      <td title={easternText(g.nextDueAt) || undefined}>
                        {dueText(g.nextDueAt)}
                        {presale && <span className="la-auto-chip neutral" title={presale.title}>{presale.text}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="la-hint">No group of this event is on Auto. An admin turns it on in a group's editor (it needs a floor).</div>
          )}
          {others.length > 0 && (
            <div className="la-hint">
              + {plural(others.length, 'group')} on Auto on other events · soonest next run{' '}
              {soonestDue(others) ? dueText(soonestDue(others)) : 'not scheduled'}
            </div>
          )}
          {canAssignLogin && <LaDiscordCard />}
        </div>
      )}
    </div>
  );
}

export default LaAutoPanel;
